<template>
  <div ref="root" class="sp-toolbar" :class="classModifiers">
    <div ref="content">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useHost, useTemplateRef } from "vue";
import { toBoolean } from "#ce/utils/props";
import { useDynamicOffsetTracking } from "./composables/useDynamicOffsetTracking.js";
import { scheduleFragmentLandingCorrection } from "./fragment-landing-correction.js";
import { props as toolbarProps } from "./props.js";

const props = defineProps(toolbarProps);

const content = useTemplateRef("content");
const slotContents = computed(() => {
  if (content.value?.firstElementChild?.tagName === "SLOT") {
    return content.value.firstElementChild.assignedElements();
  }
  return Array.from(content.value?.children ?? []);
});

const slotContent = computed(() => (slotContents.value ?? []).at(0));

const stickToTop = ref(false);
const root = useTemplateRef("root");
const hostElement = useHost();

let observer = null;
let resizeObserver = null;
let scrollPaddingBasePx = null;

function offsetElement() {
  return props.offsetSelector ? document.querySelector(props.offsetSelector) : null;
}

function usesDynamicOffset() {
  return toBoolean(props.dynamicOffset) && Boolean(props.offsetSelector);
}

// The geometry watch runs for the component's whole lifetime (not just while stuck): the
// stick boundary and anchor scroll padding must follow the tracked element even before the
// toolbar first sticks.
const { startGeometryWatch, startStickTracking, stopStickTracking } = useDynamicOffsetTracking({
  hostElement,
  isEnabled: usesDynamicOffset,
  resolveElement: offsetElement,
  onPinnedEdge: refreshStickBoundary,
  // A width change (rotation, window resize) may shrink the header permanently, so the
  // running scroll-padding maximum starts over.
  onViewportWidthChange: () => {
    scrollPaddingBasePx = null;
  },
});

function updateSlotContent(isSticky) {
  if (!slotContent.value) {
    return;
  }
  slotContent.value.stuckToTop = isSticky;
  slotContent.value.classList.toggle("--toolbar-is-sticky", isSticky);
}

const classModifiers = computed(() => ({
  "--is-sticky": toBoolean(props.sticky) || stickToTop.value,
  "--is-absolute": toBoolean(props.absolute),
  [`--elevation-${props.elevation}`]: props.elevation,
  [`--color-${props.color}`]: props.color,
}));

function getScrollPaddingTopProperty() {
  return window.getComputedStyle(document.documentElement).getPropertyValue("--sp-sys-scroll-padding-top-default");
}

function setScrollPaddingTopProperty() {
  if (!root.value) {
    return;
  }

  const rootElHeight = root.value.getBoundingClientRect().height;
  const offsetTop = "2rem";
  // Dynamic-offset pages base the padding on the tracked header; everywhere else it comes
  // from the static main-menu token.
  const basePadding = scrollPaddingBasePx === null ? getScrollPaddingTopProperty() : `${scrollPaddingBasePx}px`;

  document.documentElement.style.setProperty(
    "--sp-sys-scroll-padding-top",
    `calc(${basePadding} + ${rootElHeight}px + ${offsetTop})`,
  );
}

function measureStaticOffset() {
  const parsedOffset = props.offsetSelector
    ? document.querySelector(props.offsetSelector)?.clientHeight
    : Number.parseInt(props.offset);

  return parsedOffset || 0;
}

// Initialized eagerly so the sticky-top CSS fallback has a real value on first paint; the
// mounted re-measure covers an offset element that renders after this component.
const offset = ref(measureStaticOffset());

// Re-derive what hangs off the tracked element's pinned edge. On sticky-on-scroll toolbars
// that is the stick/unstick boundary (observer rootMargin) and the anchor scroll padding.
// `offset` also feeds the v-bind CSS fallback for the sticky top, so on a permanently-sticky
// dynamic toolbar the fallback follows the last pinned edge rather than the static
// mount-time measure.
function refreshStickBoundary(pinnedEdge) {
  const boundary = Math.round(pinnedEdge);

  if (offset.value !== boundary) {
    offset.value = boundary;
    if (observer) {
      rebuildStickBoundaryObserver();
    }
  }

  // The scroll padding uses the largest pinned edge seen, not the current one: the header
  // re-expands on scroll-up, so an upward anchor jump lands with the expanded header. Padding
  // for the current (collapsed) height would leave the target clipped underneath it.
  if (observer && boundary > (scrollPaddingBasePx ?? -1)) {
    scrollPaddingBasePx = boundary;
    setScrollPaddingTopProperty();
  }
}

let sentinelElement = null;

function handleSentinelIntersection([entry]) {
  const shouldStick = !entry.isIntersecting;
  if (stickToTop.value !== shouldStick) {
    stickToTop.value = shouldStick;
    updateSlotContent(shouldStick);

    if (shouldStick) {
      startStickTracking();
    } else {
      stopStickTracking();
    }
  }
}

function observeStickBoundary() {
  observer = new IntersectionObserver(handleSentinelIntersection, {
    // An explicit root, because rootMargin is ignored on the implicit root when the
    // document is not the top-level browsing context (embeds, test iframes).
    root: document,
    threshold: 0,
    // The huge bottom margin extends the root far below the viewport, so "not
    // intersecting" can only mean the sentinel was scrolled up past the offset line — a
    // below-the-fold sentinel still intersects and must not stick. It also leaves a
    // single boundary, so every crossing fires a callback; a viewport-sized root misses
    // jump scrolls from one non-intersecting side to the other.
    rootMargin: `-${offset.value}px 0px 100000% 0px`,
  });

  observer.observe(sentinelElement);
}

// rootMargin is immutable on a live IntersectionObserver, so following a header that
// grows/shrinks means recreating the observer. Recreation also delivers an initial entry,
// which re-evaluates the stuck state against the new boundary.
function rebuildStickBoundaryObserver() {
  observer.disconnect();
  observeStickBoundary();
}

function setupIntersectionObserver() {
  if (!toBoolean(props.stickyOnScroll) || observer) {
    return;
  }

  let current = hostElement;
  let sentinel = null;

  if (current.previousElementSibling?.classList.contains("sp-toolbar-sentinel")) {
    sentinel = current.previousElementSibling;
  } else {
    while (current.parentElement) {
      current = current.parentElement;
      const parentStyle = window.getComputedStyle(current);

      if (parentStyle.position === "sticky" || parentStyle.position === "fixed") {
        const sentinelBeforeParent = current.previousElementSibling;
        if (sentinelBeforeParent?.classList.contains("sp-toolbar-sentinel")) {
          sentinel = sentinelBeforeParent;
          break;
        }
      }
    }
  }

  if (!sentinel) {
    return;
  }

  sentinelElement = sentinel;

  setScrollPaddingTopProperty();

  observeStickBoundary();

  resizeObserver = new ResizeObserver(() => {
    setScrollPaddingTopProperty();
  });

  resizeObserver.observe(root.value);
}

function cleanup() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  sentinelElement = null;

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  stopStickTracking();
}

onMounted(() => {
  nextTick(() => {
    offset.value = measureStaticOffset();

    setupIntersectionObserver();
    startGeometryWatch();

    // Only the toolbar that manages the anchor scroll padding corrects fragment landings.
    if (observer) {
      scheduleFragmentLandingCorrection();
    }

    // A permanently sticky toolbar has no stick/unstick transition to key off, so start
    // tracking immediately.
    if (toBoolean(props.sticky)) {
      startStickTracking();
    }
  });
});

onBeforeUnmount(() => {
  cleanup();
});
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-toolbar {
  position: var(--position);
  background-color: var(--background-color, transparent);
  box-shadow: var(--box-shadow, none);

  &.--elevation-1 {
    --box-shadow: var(--sp-sys-elevation-bottom-1);
  }

  &.--elevation-2 {
    --box-shadow: var(--sp-sys-elevation-bottom-2);
  }

  &.--elevation-3 {
    --box-shadow: var(--sp-sys-elevation-bottom-3);
  }

  &.--color-white {
    --background-color: var(--sp-sys-color-surface);
  }

  &.--is-absolute {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  &.--is-sticky {
    position: fixed;
    top: var(--sp-comp-toolbar-sticky-top-position, v-bind("`${offset}px`"));
    left: 0;
    right: 0;
    z-index: var(--sp-comp-toolbar-sticky-z-index, 9999);
  }
}
</style>
